import { useState, useEffect, useRef } from 'react';
import { Input } from '../components/ui/input';
import { Search, X, User } from 'lucide-react';

// Fuzzy search: matches if all words in query are found anywhere in the target
const fuzzyMatch = (query, target) => {
  if (!query || !target) return false;
  const queryWords = query.toLowerCase().split(/\s+/).filter(w => w.length > 0);
  const targetLower = target.toLowerCase();
  
  // Every word in query must be found somewhere in target
  return queryWords.every(word => targetLower.includes(word));
};

export function MemberSearchInput({ 
  value, 
  onChange, 
  placeholder = "Search by name or ITS...",
  members = [],
  api,
  className = "",
  showMobile = true
}) {
  const [searchTerm, setSearchTerm] = useState(value || '');
  const [isOpen, setIsOpen] = useState(false);
  const [allMembers, setAllMembers] = useState(members);
  const [loading, setLoading] = useState(false);
  const wrapperRef = useRef(null);

  // Fetch members if not provided
  useEffect(() => {
    if (api && members.length === 0) {
      fetchMembers();
    }
  }, [api]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchMembers = async () => {
    if (!api) return;
    setLoading(true);
    try {
      // Fetch members in batches
      let allFetched = [];
      for (let page = 1; page <= 20; page++) {
        const response = await api.get(`/members?page=${page}&limit=100`);
        const fetchedMembers = response.data.members || [];
        allFetched = [...allFetched, ...fetchedMembers];
        if (fetchedMembers.length < 100) break;
      }
      setAllMembers(allFetched);
    } catch (error) {
      console.error('Failed to fetch members:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter members using fuzzy search
  const filteredMembers = searchTerm.length > 1
    ? allMembers.filter(m => 
        fuzzyMatch(searchTerm, m.full_name) ||
        fuzzyMatch(searchTerm, m.its_id) ||
        (m.mobile && m.mobile.includes(searchTerm))
      ).slice(0, 20)
    : [];

  const handleSelect = (member) => {
    const displayValue = showMobile && member.mobile 
      ? `${member.full_name} - ${member.mobile}`
      : member.full_name;
    setSearchTerm(displayValue);
    onChange(displayValue, member);
    setIsOpen(false);
  };

  const handleClear = () => {
    setSearchTerm('');
    onChange('', null);
  };

  return (
    <div ref={wrapperRef} className={`relative ${className}`}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setIsOpen(true);
            // Also update parent with raw text
            if (!filteredMembers.length) {
              onChange(e.target.value, null);
            }
          }}
          onFocus={() => setIsOpen(true)}
          placeholder={placeholder}
          className="pl-9 pr-8 h-9"
        />
        {searchTerm && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Dropdown results */}
      {isOpen && searchTerm.length > 1 && (
        <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {loading ? (
            <div className="p-3 text-center text-slate-500 text-sm">Loading members...</div>
          ) : filteredMembers.length > 0 ? (
            filteredMembers.map((member) => (
              <div
                key={member.id || member.its_id}
                className="p-2 hover:bg-slate-50 cursor-pointer flex items-center gap-2 border-b last:border-b-0"
                onClick={() => handleSelect(member)}
              >
                <div className="w-8 h-8 bg-navy/10 rounded-full flex items-center justify-center">
                  <User className="w-4 h-4 text-navy" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate">{member.full_name}</p>
                  <p className="text-xs text-slate-500">
                    ITS: {member.its_id || 'N/A'} | {member.mohalla || 'N/A'} - {member.sector || 'N/A'}
                  </p>
                </div>
                {member.mobile && (
                  <span className="text-xs text-slate-400">{member.mobile}</span>
                )}
              </div>
            ))
          ) : (
            <div className="p-3 text-center text-slate-500 text-sm">
              No members found for "{searchTerm}"
              <p className="text-xs mt-1">Type name parts in any order (e.g., "murtaza raipur")</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
